import React, {
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { Ripple } from 'primereact/ripple';
import { StyleClass } from 'primereact/styleclass';
import { Dropdown } from 'primereact/dropdown';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Environment Variables
import {
  LOCAL_STORAGE_TOKEN,
  LOCAL_STORAGE_REFRESH_TOKEN,
  VERSION,
} from '../environments/environment';

// Internal Context
import { MarsinetContext } from '../app/context/marsinetProvider';
import { ToastProvider } from '../app/context/ToastProvider';

// Internal Components
import SelectorLanguageComponent from '../app/components/selector-language-component/selector-language-component';

// Internal Static Pages
import CookiePolicy from '../app/pages/cookie-policy/cookie-policy';
import LegalNotice from '../app/pages/legal-notice/legal-notice';
import Confidentiality from '../app/pages/confidentiality/confidentiality';
import AccountNotYetActivated from '../app/pages/account-not-yet-activated/account-not-yet-activated';
import VerifyAccount from '../app/pages/verify-account/verify-account';

// Internal Non-Lazy Components
import Informe from '../app/pages/authenticated/informe/informe';
import Device from '../app/pages/authenticated/devices/device/device';
import Dashboard from '../app/pages/authenticated/dashboards/dashboard/dashboard';
import Institution from '../app/pages/authenticated/institutions/institution/institution';
import Maintenance from '../app/pages/authenticated/maintenance/maintenance';

// Internal Types
import { Menu } from '../app/typescript-axios';
import ErrorModal from '../app/components/error-modal/error-modal';
import { configureAxios } from '../app/utils/utils';
import { apiServices } from '../app/services/apiServices';
import { timezones } from '../app/constants/constants';
import { ProgressSpinner } from 'primereact/progressspinner';
import usePageTracking from '../app/hooks/usePageTracking';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import IntrasessionDetails from '../app/pages/authenticated/patients/patient/pages/intrasession-details/intrasession-details';
import AccountRegistered from '../app/pages/account-registered/account-registered';
import OtaUploadPage from '../app/pages/authenticated/ota-upload/ota-upload';
const PendingSurveys = React.lazy(
  () => import('../app/pages/authenticated/pending-surveys/pending-surveys')
);
import Surveys from '../app/pages/authenticated/surveys/surveys';
import SurveyComponent from '../app/pages/authenticated/surveys/survey/survey';
import { ROLES } from '@marsinet/commons';
import VideosPage from '../app/pages/authenticated/videos/videos';
import { Incidents } from '../app/pages/authenticated/incidences/incidences';
import { DetailsIncident } from '../app/pages/authenticated/incidences/components/incidences/details-incidence';
import DetailsMaintenanceOrder from '../app/pages/authenticated/incidences/components/maintenance-orders/details-maintenance-order';

// Internal Lazy Load Pages
const Login = React.lazy(() => import('../app/pages/login/login'));
const Register = React.lazy(() => import('../app/pages/register/register'));
const RequestResetPassword = React.lazy(
  () => import('../app/pages/request-reset-password/request-reset-password')
);
const ResetPassword = React.lazy(
  () => import('../app/pages/reset-password/reset-password')
);

const Patients = React.lazy(
  () => import('../app/pages/authenticated/patients/patients')
);
const Patient = React.lazy(
  () => import('../app/pages/authenticated/patients/patient/patient')
);
const Sessions = React.lazy(
  () => import('../app/pages/authenticated/sessions/sessions')
);
const Dashboards = React.lazy(
  () => import('../app/pages/authenticated/dashboards/dashboards')
);
const Home = React.lazy(() => import('../app/pages/authenticated/home/home'));
const MyProfile = React.lazy(
  () => import('../app/pages/authenticated/my-profile/my-profile')
);
const ErrorPage = React.lazy(() => import('../app/pages/error/error-page'));
const DeleteAccountPage = React.lazy(
  () => import('../app/pages/request-delete-account/request-delete-account')
);
const Users = React.lazy(
  () => import('../app/pages/authenticated/users/users')
);
const Devices = React.lazy(
  () => import('../app/pages/authenticated/devices/devices')
);
const Institutions = React.lazy(
  () => import('../app/pages/authenticated/institutions/institutions')
);
const ControlPanel = React.lazy(
  () => import('../app/pages/authenticated/control_panel/control_panel')
);

const NotPremiumPage = React.lazy(
  () => import('../app/pages/not-premium/not-premium-page')
);

export function AppRoutes() {
  const {
    setIsLogged,
    setAccessToken,
    access_token,
    refresh_token,
    setRefreshToken,
    setSelectedTimezone,
    selectedTimezone,
    setError,
    role,
    setRole,
    setAdmin,
  } = useContext(MarsinetContext);
  const [menu, setMenu] = useState<Menu[]>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const menuBtnRef = useRef(null);
  // Mantiene los clics dentro del menú para que no se cierre al usar el selector de zona horaria.
  const userSettingsMenuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userSettingsMenuOpen, setUserSettingsMenuOpen] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [personalPatient, setPersonalPatient] = useState<string>('');
  const [isExchangingOtt, setIsExchangingOtt] = useState<boolean>(() =>
    new URLSearchParams(window.location.search).has('ott')
  ); //estado para saber si se está intercambiando un OTT

  const ottHandled = useRef(false);
  const intendedPath = useRef<string>(window.location.pathname);

  const closeMenu = () => {
    document.getElementById('app-sidebar').classList.add('hidden');
  };

  const signOut = () => {
    localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
    setIsLogged(false);
    setAccessToken(null);
    setRefreshToken(null);
    setRole(null);
    setAdmin(false);
    logEvent(analytics, 'logout_marsinet', {
      access_token: access_token,
    });
    axios.defaults.headers.common['Authorization'] = null;
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ott = params.get('ott');

    if (!ott) return;
    if (ottHandled.current) return;
    ottHandled.current = true;

    const exchangeOtt = async () => {
      try {
        const response = await apiServices.authApi.authControllerExchangeOtt({
          ott,
        });
        const { access_token, refresh_token } = response.data;

        localStorage.setItem(LOCAL_STORAGE_TOKEN, access_token);
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, refresh_token);
        configureAxios(access_token);
        setAccessToken(access_token);
        setRefreshToken(refresh_token);

        navigate(intendedPath.current, { replace: true });
      } catch (error) {
        // OTT invalid or expired — just let the user land on login normally
        console.error('OTT exchange failed', error);
        navigate(`/login`, { replace: true });
      } finally {
        setIsExchangingOtt(false);
      }
    };

    exchangeOtt();
  }, []);

  useEffect(() => {
    const bootstrapSession = async () => {
      if (isExchangingOtt) return;

      if (!access_token) {
        setIsLoading(false);
        return;
      }

      configureAxios(access_token);

      try {
        const [menuResponse, accountResponse] = await Promise.all([
          apiServices.myAccountApi.myAccountControllerGetMenu(),
          apiServices.myAccountApi.myAccountControllerGetData(),
        ]);

        const { menu, is_active, premium } = menuResponse.data;

        setMenu(menu);
        setIsActive(is_active);
        setIsPremium(premium);

        setRole(accountResponse.data.organization?.role);
        setAdmin(accountResponse.data.can_edit);

        setIsLoading(false);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
        setIsLoading(false);
      }
    };

    bootstrapSession();
  }, [access_token, refresh_token, isExchangingOtt]);

  useEffect(() => {
    if (access_token && refresh_token) {
      configureAxios(access_token);
    }
  }, [access_token, refresh_token]);

  useEffect(() => {
    // Cierra los ajustes solo si el clic queda fuera del menú y de su desplegable.
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userSettingsMenuRef.current &&
        event.target instanceof Node &&
        !userSettingsMenuRef.current.contains(event.target)
      ) {
        setUserSettingsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTimezoneChange = (timezone) => {
    // La selección solo dura esta sesión; el contexto actualiza formatos y conversiones del frontend.
    setSelectedTimezone(
      timezones.some(({ value }) => value === timezone)
        ? timezone
        : Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    );
  };

  const handleClic = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    if (role === ROLES.PERSONAL) {
      const fetchPatients = async () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any =
            await apiServices.patientApi.patientControllerFindAll();
          setPersonalPatient(response.data.data[0].p_id);
        } catch (error) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      };

      fetchPatients();
    }
  }, [role]);

  usePageTracking(); // Esto rastrea la ubicación y envía el evento de vista de página a GA4

  if (isExchangingOtt || isLoading) {
    return (
      <ToastProvider>
        <ErrorModal />
        <div className="flex justify-content-center align-items-center min-h-screen">
          <ProgressSpinner />
        </div>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <ErrorModal />

      {access_token && (
        <div
          className="min-h-screen flex relative"
          style={{
            background: 'linear-gradient(60deg, #13dfb6 0%,#004e78 100%)',
          }}
        >
          {/* Menu Lateral */}
          <div
            id="app-sidebar"
            className="surface-section h-full hidden flex-shrink-0 absolute left-0 top-0 z-5 border-right-1 surface-border select-none"
            style={{ width: '258px' }}
          >
            <div
              className="flex flex-column h-full fixed py-2"
              style={{ width: '258px', overflowY: 'auto' }}
            >
              {/* Logo Aplicación */}
              <div className="flex align-items-center px-2 flex-shrink-0">
                <img
                  src="/assets/marsi-azul-degradado-fondo-blanco.webp"
                  alt="Logotipo de Marsinet"
                  rel="preload"
                  style={{ width: '240px' }}
                />
              </div>

              {/* Menu */}
              <div className="flex-grow-1 overflow-auto">
                <ul className="list-none p-0 m-0 overflow-hidden">
                  {menu?.map((item: Menu, index: number) => {
                    return (
                      <li key={index}>
                        <NavLink
                          className={({ isActive, isPending }) => {
                            return isPending
                              ? 'p-ripple flex align-items-center gap-2 cursor-pointer p-3 text-700 hover:bg-primary transition-duration-150 transition-colors w-full no-underline'
                              : isActive
                                ? 'p-ripple flex align-items-center gap-2 cursor-pointer p-3 text-700 hover:bg-primary transition-duration-150 transition-colors w-full no-underline bg-primary'
                                : 'p-ripple flex align-items-center gap-2 cursor-pointer p-3 text-700 hover:bg-primary transition-duration-150 transition-colors w-full no-underline';
                          }}
                          end={index === 0}
                          to={
                            role === ROLES.PERSONAL &&
                              item?.to === '/authenticated/patients'
                              ? `/authenticated/patients/${personalPatient}`
                              : item?.to
                          }
                          onClick={() => closeMenu()}
                        >
                          {item.i_class_name !== 'pi pi-android mr-2' && (
                            <i className={item?.i_class_name}></i>
                          )}

                          {item.i_class_name === 'pi pi-android mr-2' && (
                            <img
                              src="/assets/robot.svg"
                              alt="exo"
                              loading="lazy"
                              style={{
                                width: '20px',
                                marginRight: '7px',
                                filter: 'invert(50%)',
                              }}
                              className="p-ripple flex align-items-center cursor-pointer"
                            />
                          )}

                          <span className="font-medium flex-1 white-space-normal line-height-3">
                            {t(item?.translation)}
                          </span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <footer className="flex justify-content-center align-items-center py-9">
                {`version ${VERSION}`}
              </footer>
            </div>
          </div>

          {/* Container */}
          <div className="w-full">
            {/* Header */}
            <div
              className="flex justify-content-between align-items-center surface-0 border-bottom-1 surface-border fixed z-4 w-full"
              style={{ height: '70px' }}
            >
              {/* Boton menu */}
              <div className="w-full">
                <div className="flex justify-content-start ml-1">
                  <StyleClass
                    nodeRef={menuBtnRef}
                    selector="#app-sidebar"
                    enterClassName="hidden"
                    enterActiveClassName="fadeinleft"
                    leaveToClassName="hidden"
                    leaveActiveClassName="fadeoutleft"
                    hideOnOutsideClick
                  >
                    <div
                      ref={menuBtnRef}
                      className="p-ripple cursor-pointer block text-700"
                      onClick={handleClic}
                    >
                      <span className="p-ripple flex cursor-pointer p-3 border-round text-700 hover:surface-200 transition-duration-150 transition-colors w-full no-underline">
                        <i className="pi pi-bars text-3xl sm:text-4xl" />
                        <Ripple />
                      </span>
                    </div>
                  </StyleClass>
                </div>
              </div>

              {/* Logo */}
              <div className="w-full text-center mx-3">
                <img
                  src="/assets/marsi-azul-degradado-fondo-blanco-plano.webp"
                  alt="logo marsinet"
                  rel="preload"
                  className="hidden sm:block w-64 cursor-pointer mx-auto"
                  style={{ width: '350px', cursor: 'pointer' }}
                  onClick={() => (window.location.href = '/authenticated')}
                />
                <img
                  src="/assets/marsi-azul-degradado-pequenio.webp"
                  alt="Logo Pequeño"
                  className="block sm:hidden w-6 h-6 cursor-pointer mx-auto"
                  onClick={() => (window.location.href = '/authenticated')}
                />
              </div>

              {/* Menu Usuario */}
              <div className="w-full">
                <div
                  ref={userSettingsMenuRef}
                  className="flex justify-content-end mr-3 relative"
                >
                  <div
                    className="cursor-pointer block text-700"
                    onClick={() =>
                      setUserSettingsMenuOpen((isOpen) => !isOpen)
                    }
                    role="button"
                    tabIndex={0}
                    aria-expanded={userSettingsMenuOpen}
                    aria-haspopup="menu"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setUserSettingsMenuOpen((isOpen) => !isOpen);
                      }
                    }}
                  >
                    <span className="flex items-center justify-center cursor-pointer border-round text-700 hover:surface-200 transition duration-150 transition-colors no-underline">
                      <i className="pi pi-user text-3xl sm:text-4xl" />
                      <Ripple />
                    </span>
                  </div>

                  {/* Menu Desplegable Reducido */}
                  <ul
                    className={`list-none p-0 m-0 ml-5 ${
                      userSettingsMenuOpen ? 'fadein' : 'hidden'
                    } select-none surface-section border-1 surface-border
                    right-0 top-100 z-4 shadow-2 absolute max-w-xs`}
                    style={{ width: '20rem' }}
                    role="menu"
                  >
                    {/* Selector de Idioma */}
                    <li className="ml-0">
                      <div className="flex items-center gap-2 p-2 text-700 hover:surface-200 transition duration-150">
                        <SelectorLanguageComponent />
                      </div>
                    </li>

                    {/* Zona Horaria */}
                    <li className="ml-0">
                      <div className="flex items-center gap-2 p-2 text-700 hover:surface-200 transition duration-150">
                        <i className="pi pi-globe font-medium text-lg mr-3 pt-3"></i>
                        <Dropdown
                          id="timezone-select"
                          value={selectedTimezone}
                          onChange={(e) => handleTimezoneChange(e.value)}
                          options={timezones}
                          optionLabel="label"
                          placeholder="Select your timezone"
                          filter
                          appendTo="self"
                          style={{ width: '260px', minWidth: 0 }}
                          className="h-6 text-sm truncate"
                        />
                      </div>
                    </li>

                    {/* Mi Cuenta */}
                    <li className="ml-0">
                      <NavLink
                        to="/authenticated/my-profile"
                        className="flex items-center gap-2 p-2 text-700 hover:surface-200 transition duration-150 no-underline"
                      >
                        <i className="pi pi-cog mr-3 pt-1"></i>
                        <span className="font-medium">
                          {t('pages.menu.myAccount')}
                        </span>
                      </NavLink>
                    </li>

                    {/* Cerrar Sesión */}
                    <li className="ml-0">
                      <NavLink
                        to="/login"
                        onClick={() => signOut()}
                        className="flex items-center gap-2 p-2 text-700 hover:surface-100 transition duration-150 no-underline"
                      >
                        <i className="pi pi-sign-out mr-3 pt-1"></i>
                        <span className="font-medium">
                          {t('pages.menu.signOut')}
                        </span>
                      </NavLink>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contenido */}
            <div className="mt-7">
              <div className="col-12 py-4 p-1 md:p-3">
                <div className="flex justify-content-center">
                  <div className="xl:max-width w-full">
                    <div className="grid grid-nogutter">
                      <Suspense fallback={<ProgressSpinner />}>
                        <Routes>
                          {/* Públicas también accesibles estando logueado */}
                          <Route
                            path="/verify-account"
                            element={<VerifyAccount />}
                          />
                          <Route
                            path="/account-registered"
                            element={<AccountRegistered />}
                          />
                          <Route
                            path="/legal-notice"
                            element={<LegalNotice />}
                          />
                          <Route
                            path="/confidentiality"
                            element={<Confidentiality />}
                          />
                          <Route
                            path="/cookies-policy"
                            element={<CookiePolicy />}
                          />
                          <Route
                            path="/login"
                            element={<Navigate to="/authenticated" replace />}
                          />

                          {/* Redirección inicial */}
                          <Route
                            path="/"
                            element={<Navigate to="/authenticated" replace />}
                          />

                          {/* Cuenta activa o no activa */}
                          <Route
                            path="/authenticated"
                            element={
                              !isActive ? (
                                <AccountNotYetActivated is_active={isActive} />
                              ) : (
                                <Home />
                              )
                            }
                          />

                          {/* Rutas protegidas por isActive */}
                          {isActive && (
                            <Route
                              path="/authenticated/patients"
                              element={
                                isPremium ? <Patients /> : <NotPremiumPage />
                              }
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/patients/:patient_id"
                              element={
                                isPremium ? <Patient /> : <NotPremiumPage />
                              }
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/patients/:patient_id/intrasession/:ingestion_id"
                              element={<IntrasessionDetails />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/users"
                              element={<Users />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/devices"
                              element={<Devices />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/maintenance"
                              element={<Maintenance />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/devices/:device_id"
                              element={<Device />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/sessions"
                              element={
                                isPremium ? <Sessions /> : <NotPremiumPage />
                              }
                            />
                          )}
                          {isActive && (
                            <Route
                              path="/authenticated/dashboards"
                              element={<Dashboards />}
                            />
                          )}
                          {isActive && (
                            <Route
                              path="/authenticated/dashboards/:dashboard_id"
                              element={<Dashboard />}
                            />
                          )}
                          {isActive && (
                            <Route
                              path="/authenticated/incidents"
                              element={<Incidents />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/incidents/:id_incident"
                              element={<DetailsIncident />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/maintenance-order/:id_om"
                              element={<DetailsMaintenanceOrder />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/patients/:patient_id/:start_date/:end_date"
                              element={<Informe />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/institutions"
                              element={<Institutions />}
                            />
                          )}
                          {isActive && (
                            <Route
                              path="/authenticated/control_panel"
                              element={<ControlPanel />}
                            />
                          )}

                          {isActive && (
                            <Route
                              path="/authenticated/institutions/:id_institution"
                              element={<Institution />}
                            />
                          )}
                          {isActive && (
                            <Route
                              path="/authenticated/ota-upload"
                              element={<OtaUploadPage />}
                            />
                          )}
                          <Route
                            path="/authenticated/surveys"
                            element={<Surveys />}
                          />

                          <Route
                            path="/authenticated/my-profile"
                            element={<MyProfile />}
                          />
                          <Route
                            path="/authenticated/pending-surveys"
                            element={<PendingSurveys />}
                          />

                          <Route
                            path="/authenticated/surveys/:survey_id"
                            element={<SurveyComponent />}
                          />

                          <Route path="*" element={<ErrorPage />} />

                          <Route
                            path="request-delete-account"
                            element={<DeleteAccountPage />}
                          />
                          <Route path="/authenticated/videos" element={<VideosPage />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!access_token && (
        <Suspense fallback={<ProgressSpinner />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/request-reset-password"
              element={<RequestResetPassword />}
            />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Públicas */}
            <Route path="/legal-notice" element={<LegalNotice />} />
            <Route path="/confidentiality" element={<Confidentiality />} />
            <Route path="/cookies-policy" element={<CookiePolicy />} />
            <Route path="/verify-account" element={<VerifyAccount />} />
            <Route path="/account-registered" element={<AccountRegistered />} />

            {/* Si se abre un enlace profundo protegido desde email sin sesión, redirigir a login */}
            <Route
              path="/authenticated/*"
              element={<Navigate to="/login" replace />}
            />

            <Route
              path="request-delete-account"
              element={<DeleteAccountPage />}
            />
            <Route path="*" element={<ErrorPage />} />
          </Routes>
        </Suspense>
      )}
    </ToastProvider>
  );
}

export default AppRoutes;
